import { useCallback, useMemo } from 'react';
import { useDroppable } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import type {
  CanonicalMeasurement,
  MeasurementGroup,
  MeasurementLabel,
} from '@/models/template';
import sectionStyles from '@/features/report/Measurements/Section/MeasurementSection.module.css';
import { AddButton } from '../primitives/AddButton';
import { EditableText } from '../primitives/EditableText';
import { indexToLetter } from '../primitives/format';
import { EditableMeasurementRow } from './EditableMeasurementRow';
import { GROUP_DROP_PREFIX } from './measurementsDnd';
import styles from './EditableMeasurementsSection.module.css';

interface EditableMeasurementGroupProps {
  group: MeasurementGroup;
  position: number;
  canonicalMeasurementsByKey: Map<string, CanonicalMeasurement>;
  canonicalMeasurements: CanonicalMeasurement[];
  usedKeys: Set<string>;
  onChange: (group: MeasurementGroup) => void;
}

export function EditableMeasurementGroup({
  group,
  position,
  canonicalMeasurementsByKey,
  canonicalMeasurements,
  usedKeys,
  onChange,
}: EditableMeasurementGroupProps) {
  const sortedRows = useMemo(
    () => [...group.measurementLabels].sort((a, b) => a.index - b.index),
    [group.measurementLabels]
  );

  const handleRowChange = useCallback(
    (uuid: string, next: MeasurementLabel) => {
      const copy = sortedRows.map((r) => (r.uuid === uuid ? next : r));
      onChange({ ...group, measurementLabels: copy });
    },
    [group, onChange, sortedRows]
  );

  const handleRowDelete = useCallback(
    (uuid: string) => {
      onChange({
        ...group,
        measurementLabels: sortedRows.filter((r) => r.uuid !== uuid),
      });
    },
    [group, onChange, sortedRows]
  );

  const handleAddRow = useCallback(() => {
    const maxIndex = sortedRows.reduce((m, ml) => Math.max(m, ml.index), -1);
    const now = new Date().toISOString();
    const newLabel: MeasurementLabel = {
      uuid: crypto.randomUUID(),
      label: '',
      canonicalMeasurement: null,
      canonicalMeasurementKey: null,
      index: maxIndex + 1,
      precision: null,
      unit: null,
      createdAt: now,
      updatedAt: now,
    };
    onChange({ ...group, measurementLabels: [...sortedRows, newLabel] });
  }, [group, onChange, sortedRows]);

  // Make the entire group container droppable so empty groups (or the
  // space below the last row) can accept items dragged from another group.
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `${GROUP_DROP_PREFIX}${group.uuid}`,
  });

  return (
    <div className={sectionStyles.section}>
      <div className={sectionStyles.header}>
        <span className={sectionStyles.accentBar} />
        <span className={sectionStyles.title}>
          {position}.{' '}
          <EditableText
            value={group.label}
            onChange={(label) => onChange({ ...group, label })}
            placeholder="Group name"
          />
        </span>
      </div>
      <SortableContext
        items={sortedRows.map((r) => r.uuid)}
        strategy={verticalListSortingStrategy}
      >
        <div
          ref={setDropRef}
          className={`${styles.editableContent} ${
            isOver ? styles.dropTarget : ''
          }`}
        >
          {sortedRows.map((ml, idx) => (
            <EditableMeasurementRow
              key={ml.uuid}
              measurementLabel={ml}
              position={indexToLetter(idx)}
              selectedCanonicalMeasurement={
                ml.canonicalMeasurementKey
                  ? (canonicalMeasurementsByKey.get(
                      ml.canonicalMeasurementKey
                    ) ?? null)
                  : null
              }
              canonicalMeasurements={canonicalMeasurements}
              usedKeys={usedKeys}
              onMeasurementLabelChange={(next) =>
                handleRowChange(ml.uuid, next)
              }
              onDelete={() => handleRowDelete(ml.uuid)}
            />
          ))}
          {sortedRows.length === 0 && (
            <div className={styles.emptyDropHint}>Drop measurement here</div>
          )}
        </div>
      </SortableContext>
      <div className={styles.addRowWrap}>
        <AddButton onClick={handleAddRow} label="Add row" />
      </div>
    </div>
  );
}
